/**
 * HID 设备通信协议处理模块
 * 包含帧格式定义、校验和计算、命令构造和响应解析
 * 协议格式: AA 53 LENGTH COMMAND DATA... CHECKSUM
 */
import { CommandType, type DeviceStatus, type DeviceDetail } from './types'

// 协议帧头标识
const FRAME_HEADER: [number, number] = [0xAA, 0x53]

/**
 * 计算协议帧校验和
 * 算法：累加帧头和所有数据字节后，取 16 位补码
 */
export function calculateChecksum(data: number[]): string {
  let sum = FRAME_HEADER[0] + FRAME_HEADER[1]

  for (const byte of data) {
    const value = typeof byte === 'string' ? parseInt(byte, 16) : byte
    if (!isNaN(value) && value >= 0 && value <= 0xFF) {
      sum += value
    }
  }

  const sum16bit = sum & 0xFFFF
  const checksum = (~sum16bit + 1) & 0xFF

  return checksum.toString(16).padStart(2, '0').toUpperCase()
}

/**
 * 构建协议命令帧
 * @param command - 命令类型
 * @param data - 命令数据数组
 * @returns 完整的 Uint8Array 帧
 */
export function buildCommand(
  command: CommandType,
  data: number[] = []
): Uint8Array {
  // 对齐旧版: LENGTH 等于整帧字节长度 (AA 53 LENGTH CMD DATA... CHECKSUM)
  const length = data.length + 5 // 帧头(2) + 长度(1) + 命令(1) + 数据(n) + 校验和(1)
  const frameData = [length, command, ...data]
  const checksum = calculateChecksum(frameData)
  const checksumValue = parseInt(checksum, 16)

  return new Uint8Array([...FRAME_HEADER, ...frameData, checksumValue])
}

/**
 * 将十六进制字符串转换为 Uint8Array
 * @param hexString - 格式: "AA 53 08 80 00 01 00 7A"（空格分隔）
 * @returns 转换后的字节数组
 */
export function hexStringToBytes(hexString: string): Uint8Array {
  const hexArray = hexString.trim().split(/\s+/)
  return new Uint8Array(hexArray.map(hex => parseInt(hex, 16)))
}

/**
 * 将字节数组转换为十六进制字符串
 * @param bytes - 字节数组
 * @returns 十六进制字符串（大写字母，空格分隔）
 */
export function bytesToHexString(bytes: Uint8Array | number[]): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ')
}

/**
 * 解析设备状态响应
 * 从响应帧中提取设备的各项状态参数
 * @param hexString - 十六进制响应帧字符串
 * @returns DeviceStatus 对象或 null（如果解析失败）
 */
export function parseHeadsetResponse(hexString: string): DeviceStatus | null {
  const bytes = hexStringToBytes(hexString)

  // 验证最小长度
  if (bytes.length < 8) {
    console.warn('响应长度过短:', bytes.length)
    return null
  }

  // 验证帧头
  if (bytes[0] !== FRAME_HEADER[0] || bytes[1] !== FRAME_HEADER[1]) {
    console.warn('无效的帧头:', bytes[0], bytes[1])
    return null
  }

  // 验证校验和
  const payload = Array.from(bytes.slice(2, -1))
  const receivedChecksum = bytes[bytes.length - 1]
  const calculatedChecksum = parseInt(calculateChecksum(payload), 16)

  if (receivedChecksum !== calculatedChecksum) {
    console.warn('校验和不匹配:', receivedChecksum, calculatedChecksum)
    return null
  }

  const B = bytes.slice(6, 6 + 54)

  const parseHighBitValueByte = (byteValue: number) => {
    const raw = Number(byteValue) || 0
    return {
      raw,
      enabled: (raw & 0x80) ? 1 : 0,
      value: raw & 0x7F,
    }
  }

  // 对齐旧项目 server-1.js parsePackedVersion：小端 packed version -> Vx.y.z
  const parsePackedVersion = (hi: number, lo: number): string => {
    if ((hi === 0 && lo === 0) || hi == null || lo == null) return ''
    const word = (lo << 8) | hi
    const major = (word >> 12) & 0x0F
    const minor = (word >> 7) & 0x1F
    const patch = word & 0x7F
    return `V${major}.${minor}.${patch}`
  }

  // 解析耳返状态 (B[8])
  const sidetoneRaw = B[8] || 0
  const sidetoneEnabled = (sidetoneRaw & 0x80) ? 1 : 0  // bit7: 耳返开关
  const sidetoneVolume = sidetoneRaw & 0x7F              // bit0-6: 耳返音量 0-100

  // 解析蓝牙详细状态 (B[18])
  const bluetoothStatusByte = B[18] || 0
  const bluetoothConnected = (bluetoothStatusByte & 0x01) ? 1 : 0  // bit0: 蓝牙连接
  const bluetoothInCall = (bluetoothStatusByte & 0x02) ? 1 : 0     // bit1: 蓝牙通话中
  const bluetoothPlaying = (bluetoothStatusByte & 0x04) ? 1 : 0    // bit2: 蓝牙播放中

  // 解析音频流状态 (B[44])
  const audioStreamByte = B[44] || 0
  const dongleAudioPlaying = (audioStreamByte & 0x01) ? 1 : 0      // bit0: Dongle音频播放
  const headsetAudioPlaying = (audioStreamByte & 0x02) ? 1 : 0     // bit1: 耳机音频播放

  // 解析硬件静音状态 (B[45])
  const hardwareMuteByte = B[45] || 0
  const hardwareMuted = (hardwareMuteByte & 0x01) ? 1 : 0          // bit0: 硬件静音

  // 降噪状态字节：B[10] bit7 为开关，bit0-bit6 为 1-6 档位。
  // B[9] 是蓝牙分享状态，不能当作降噪档位读取。
  const noiseByte = B[10] || 0
  const noiseReductionEnabled = (noiseByte & 0x80) ? 1 : 0
  const noiseReductionMode = noiseByte & 0x7F

  // EQ状态在USB和2.4G链路中分别保存：bit7为开关，低7位为模式。
  const usbEqTag = B[12] || 0
  const usbEqControlByte = B[38] || 0
  const dongleEqTag = B[39] || 0
  const dongleEqControlByte = B[40] || 0
  const usbEqEnabled = (usbEqControlByte & 0x80) ? 1 : 0
  const usbEqMode = usbEqControlByte & 0x7F
  const dongleEqEnabled = (dongleEqControlByte & 0x80) ? 1 : 0
  const dongleEqMode = dongleEqControlByte & 0x7F
  const usbChickenByte = B[6] || 0
  const dongleChickenByte = B[41] || 0
  const usbMic = parseHighBitValueByte(B[1])
  const usbSpeaker = parseHighBitValueByte(B[4])
  const dongleMic = parseHighBitValueByte(B[42])
  const dongleSpeaker = parseHighBitValueByte(B[43])

  return {
    headsetConnected: B[0] || 0,
    // 默认字段保持 USB 链路兼容；2.4G 链路由 deviceStore 选择 dongle 字段。
    micVolumeLevel: usbMic.value,
    micMute: usbMic.enabled,
    speakerVolumeLevel: usbSpeaker.value,
    speakerMute: usbSpeaker.enabled,
    usbMicByte: usbMic.raw,
    usbMicMute: usbMic.enabled,
    usbMicVolumeLevel: usbMic.value,
    dongleMicByte: dongleMic.raw,
    dongleMicMute: dongleMic.enabled,
    dongleMicVolumeLevel: dongleMic.value,
    usbSpeakerByte: usbSpeaker.raw,
    usbSpeakerMute: usbSpeaker.enabled,
    usbSpeakerVolumeLevel: usbSpeaker.value,
    dongleSpeakerByte: dongleSpeaker.raw,
    dongleSpeakerMute: dongleSpeaker.enabled,
    dongleSpeakerVolumeLevel: dongleSpeaker.value,
    splitVolume: B[3] || 0,
    callBridgeEnabled: B[5] === 1 ? 1 : 0,
    btShareEnabled: B[9] === 1 ? 1 : 0,
    chickenOneKey: usbChickenByte,
    chickenTag: usbChickenByte & 0x7F,
    usbChickenOneKey: (usbChickenByte & 0x80) ? 1 : 0,
    usbChickenTag: usbChickenByte & 0x7F,
    dongleChickenOneKey: (dongleChickenByte & 0x80) ? 1 : 0,
    dongleChickenTag: dongleChickenByte & 0x7F,
    noiseReductionMode: noiseReductionMode || 1,
    noiseReductionEnabled,
    noiseByte,
    ledMode: B[11] || 0,
    eqMode: usbEqMode,
    eqEnabled: usbEqEnabled,
    eqTag: usbEqTag,
    eqControlByte: usbEqControlByte,
    usbEqEnabled,
    usbEqMode,
    usbEqTag,
    dongleEqEnabled,
    dongleEqMode,
    dongleEqTag,
    linkStatus: B[13] || 0,
    batteryLevel: B[14] || 0,
    powerOffStatus: B[15] || 0,
    headsetColor: B[16] || 0,
    batteryCharging: B[17] || 0,
    bluetoothConnectionMark: B[18] || 0,
    productType: B[19] || 0,
    dongleVersion: parsePackedVersion(B[28] || 0, B[29] || 0),
    softwareVersion: parsePackedVersion(B[30] || 0, B[31] || 0),
    bluetoothVersion: parsePackedVersion(B[32] || 0, B[33] || 0),
    noiseCancelVersion: parsePackedVersion(B[34] || 0, B[35] || 0),
    audioChannelMode: B[36] || 0,
    mixVolume: B[37] ?? B[2] ?? 0,
    // 新增字段
    sidetoneEnabled,
    sidetoneVolume,
    bluetoothConnected,
    bluetoothInCall,
    bluetoothPlaying,
    dongleAudioPlaying,
    headsetAudioPlaying,
    hardwareMuted,
  }
}

/**
 * 解析设备详情响应
 * 从响应帧中提取设备名称、版本等信息
 * @param hexString - 十六进制响应帧字符串
 * @returns DeviceDetail 对象或 null（如果解析失败）
 */
export function parseDeviceDetailResponse(hexString: string): DeviceDetail | null {
  const bytes = hexStringToBytes(hexString)
  if (bytes.length < 20) return null
  if (bytes[0] !== FRAME_HEADER[0] || bytes[1] !== FRAME_HEADER[1]) return null

  const totalLength = bytes[2]
  const cmd = bytes[3]
  if (cmd !== 0x82) return null

  const payload = Array.from(bytes.slice(2, -1))
  const receivedChecksum = bytes[bytes.length - 1]
  const calculatedChecksum = parseInt(calculateChecksum(payload), 16)
  if (receivedChecksum !== calculatedChecksum) return null

  // 对齐旧项目：0x82 数据区从第 6 字节开始，长度 54 字节
  const B = bytes.slice(6, 6 + 54)
  if (B.length < 28 || totalLength < 8) return null

  const brandLetters = String.fromCharCode(B[0], B[1], B[2]).replace(/\0/g, '-')
  const brandNumber = String(B[3] || 0).padStart(3, '0')
  const brandCode = `${brandLetters}${brandNumber}`

  const categoryLetters = String.fromCharCode(B[4], B[5], B[6]).replace(/\0/g, '-')
  const categoryNumber = String(B[7] || 0).padStart(3, '0')
  const categoryStr = `${categoryLetters}${categoryNumber}`

  let deviceCategory: DeviceDetail['deviceCategory'] = 'unknown'
  if (categoryLetters.startsWith('MIC')) deviceCategory = 'microPhone'
  else if (categoryLetters.startsWith('HEA') || categoryLetters.startsWith('HPH')) deviceCategory = 'headPhone'

  const modelLetters = String.fromCharCode(B[8], B[9]).replace(/\0/g, '-')
  const modelNumbers = String.fromCharCode(B[10], B[11]).replace(/\0/g, '0')
  const modelCode = `${modelLetters}${modelNumbers}`

  const configByte = B[12] || 0
  const colorValue = B[13] || 0
  const pid = ((B[14] || 0) << 8) | (B[15] || 0)
  const vid = ((B[16] || 0) << 8) | (B[17] || 0)

  const parsePackedVersion = (hi: number, lo: number): string => {
    if ((hi === 0 && lo === 0) || hi == null || lo == null) return ''
    const word = (lo << 8) | hi
    const major = (word >> 12) & 0x0F
    const minor = (word >> 7) & 0x1F
    const patch = word & 0x7F
    return `V${major}.${minor}.${patch}`
  }

  return {
    deviceCategory,
    vendorId: vid,
    productId: pid,
    deviceName: modelCode || categoryStr || brandCode,
    deviceSerial: Array.from(B.slice(20, 28)).map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(''),
    firmwareVersion: '',
    hardwareVersion: parsePackedVersion(B[18] || 0, B[19] || 0),
    protocolVersion: '',
    deviceTypeCode: bytesToHexString(B.slice(4, 12)).replace(/ /g, ''),
    softwareVersion: '',
    bluetoothVersion: '',
    noiseCancelVersion: '',
    dongleVersion: '',
  }
}

/**
 * 构建 EQ 命令数据
 */
export function buildEqCommand(eqValues: number[]): Uint8Array {
  // 将 EQ 增益值（-12 到 +12）映射为设备协议值
  const mappedValues = eqValues.map(gain => {
    // 将 dB 转换为设备专用值
    return Math.round((gain + 12) * 2) // 示例映射
  })

  return buildCommand(CommandType.SET_EQ, mappedValues)
}

/**
 * 构建静音命令
 */
export function buildMuteCommand(muted: boolean): Uint8Array {
  return buildCommand(CommandType.SET_MUTE, [muted ? 0x01 : 0x00])
}

/**
 * 构建麦克风静音命令
 */
export function buildMicMuteCommand(muted: boolean): Uint8Array {
  return buildCommand(CommandType.SET_MIC_MUTE, [muted ? 0x01 : 0x00])
}

/**
 * 构建获取状态命令
 * 与旧项目一致：AA 53 08 80 00 01 00 7A
 */
export function buildGetStatusCommand(): Uint8Array {
  // 固定值，与旧项目完全一致
  return hexStringToBytes('AA 53 08 80 00 01 00 7A')
}

/**
 * 构建设备详情查询命令
 * 与旧项目一致：AA 53 08 82 00 01 00 78
 */
export function buildGetDeviceDetailCommand(): Uint8Array {
  // 固定值，与旧项目完全一致
  return hexStringToBytes('AA 53 08 82 00 01 00 78')
}

// 命令十六进制字符串（需要时可直接使用）
export const COMMANDS = {
  GET_STATUS: 'AA 53 08 80 00 01 00 7A',
  GET_DEVICE_DETAIL: 'AA 53 08 82 00 01 00 78',
} as const
